/**
 * AdminAnalyticsTab Component
 * Analytics dashboard tab for admin panel
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Loader2, Users, Trophy, Target, TrendingUp, BarChart3, Ticket } from 'lucide-react';

const AdminAnalyticsTab = ({
  analytics,
  analyticsLoading,
  analyticsPeriod,
  setAnalyticsPeriod
}) => {
  if (analyticsLoading && !analytics) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#0047AB]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period Filter */}
      <Card className="bg-white dark:bg-slate-900">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Label className="text-sm text-slate-600">Period:</Label>
            <Select value={String(analyticsPeriod)} onValueChange={(v) => setAnalyticsPeriod(parseInt(v))}>
              <SelectTrigger className="w-48" data-testid="analytics-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 Days</SelectItem>
                <SelectItem value="30">Last 30 Days</SelectItem>
                <SelectItem value="90">Last 90 Days</SelectItem>
                <SelectItem value="365">Last Year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {analytics && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Users className="w-8 h-8 opacity-80" />
                  <div>
                    <p className="text-2xl font-bold">{analytics.user_stats?.new_users || 0}</p>
                    <p className="text-blue-100 text-sm">New Users</p>
                    <p className="text-blue-100 text-xs">in {analyticsPeriod} days</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Trophy className="w-8 h-8 opacity-80" />
                  <div>
                    <p className="text-2xl font-bold">{analytics.exam_stats?.total_attempts || 0}</p>
                    <p className="text-green-100 text-sm">Completed Tests</p>
                    <p className="text-green-100 text-xs">in {analyticsPeriod} days</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Target className="w-8 h-8 opacity-80" />
                  <div>
                    <p className="text-2xl font-bold">{analytics.subscription_stats?.active_subscriptions || 0}</p>
                    <p className="text-amber-100 text-sm">Active Subscriptions</p>
                    <p className="text-amber-100 text-xs">total active</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Ticket className="w-8 h-8 opacity-80" />
                  <div>
                    <p className="text-2xl font-bold">{analytics.coupon_stats?.used_coupons || 0}</p>
                    <p className="text-purple-100 text-sm">Coupons Used</p>
                    <p className="text-purple-100 text-xs">of {analytics.coupon_stats?.total}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Course Performance */}
          <Card className="bg-white dark:bg-slate-900">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#0047AB]" />
                Course Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics.track_stats && Object.keys(analytics.track_stats).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(analytics.track_stats).map(([track, data]) => (
                    <Card key={track} className="border">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <Badge 
                            variant="outline" 
                            className={track === 'PMP' || track === 'CAPM' ? 'border-blue-500 text-blue-600' : 'border-green-500 text-green-600'}
                          >
                            {track}
                          </Badge>
                          <span className={`text-sm font-medium ${
                            data.pass_rate >= 70 ? 'text-green-600' : 
                            data.pass_rate >= 50 ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {data.pass_rate}% passed
                          </span>
                        </div>
                        <div className="text-sm text-slate-600 space-y-1">
                          <p>Attempts: {data.total_attempts}</p>
                          <p>Passed: {data.passed}</p>
                        </div>
                        <div className="mt-2 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              data.pass_rate >= 70 ? 'bg-green-500' : 
                              data.pass_rate >= 50 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${data.pass_rate}%` }}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No test data available for this period</p>
              )}
            </CardContent>
          </Card>

          {/* Subscriptions by Course */}
          <Card className="bg-white dark:bg-slate-900">
            <CardHeader>
              <CardTitle className="text-base">Active Subscriptions by Course</CardTitle>
            </CardHeader>
            <CardContent>
              {analytics.subscription_stats?.by_track && Object.keys(analytics.subscription_stats.by_track).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(analytics.subscription_stats.by_track).map(([track, count]) => (
                    <div key={track} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <Badge variant="outline">{track}</Badge>
                      <span className="font-bold">{count} active</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-4">No active subscriptions</p>
              )}
            </CardContent>
          </Card>

          {/* Most Difficult Questions */}
          {analytics.question_stats?.hardest_questions && analytics.question_stats.hardest_questions.length > 0 && (
            <Card className="bg-white dark:bg-slate-900">
              <CardHeader>
                <CardTitle className="text-base">
                  Most Difficult Questions (lowest success rate)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50%]">Question</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-center">Attempts</TableHead>
                        <TableHead className="text-center">Success Rate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics.question_stats.hardest_questions.map((q, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-sm">
                            {(q.question_text || '').substring(0, 60)}...
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{q.category}</Badge>
                          </TableCell>
                          <TableCell className="text-center">{q.attempts}</TableCell>
                          <TableCell className="text-center">
                            <span className={`font-medium ${
                              q.success_rate >= 50 ? 'text-green-600' : 
                              q.success_rate >= 30 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {q.success_rate.toFixed(1)}%
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default AdminAnalyticsTab;
